import { forwardRef } from 'react';
import { Disableable, Sized } from '@optimus-web/common';
import { useTextareaAccessibility } from './useTextareaAccessibility';
import { TextareaContainerElement, TextareaPlaceholder, TextareaStyledComponent, ValueContainerElement } from './style';

export interface TextareaElementProps extends Sized, Disableable {
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>, value?: string) => void;
    onHeightChange?: (height: string) => void;
    placeholder?: React.ReactNode;
    rows?: number;
    children?: React.ReactNode;
    form?: string;
    label?: React.ReactNode;
    value?: string;
    name: string;
    id?: string;
    required?: boolean;
    error?: React.ReactNode;
    inline?: boolean;
    ariaLabel?: string;
    autoComplete?: HTMLTextAreaElement['autocomplete'];
    helper?: React.ReactNode;
    maxCharacterCount?: number;
}

export const TextareaElement = forwardRef(
    (
        {
            name,
            id,
            value = '',
            placeholder,
            children,
            rows,
            onChange,
            size,
            disabled,
            required,
            error,
            inline,
            ariaLabel,
            helper,
            label,
            maxCharacterCount,
            ...other
        }: TextareaElementProps,
        ref: React.Ref<HTMLTextAreaElement>
    ) => {
        // Use the accessibility hook for all accessibility-related logic
        const { accessibleLabelId, accessibleDescriptionId, accessibilityMarkup } = useTextareaAccessibility({
            name,
            label,
            helper,
            error,
            maxCharacterCount,
            value,
            ariaLabel,
        });

        const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (onChange) {
                onChange(event, event.target.value);
            }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            event.stopPropagation();
        };

        const textareaProps = {
            ...other,
            ref,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            value,
            rows,
            inputSize: size,
            disabled,
            id: name,
            required,
            error,
            name,
            helper,
            'aria-invalid': !!error,
            'aria-labelledby': accessibleLabelId,
            'aria-label': !label && ariaLabel ? ariaLabel : undefined,
            'aria-required': required,
            'aria-describedby': accessibleDescriptionId,
        };

        return (
            <TextareaContainerElement>
                <ValueContainerElement>
                    {!value && (
                        <TextareaPlaceholder inputSize={size} disabled={disabled}>
                            {placeholder}
                        </TextareaPlaceholder>
                    )}
                    <TextareaStyledComponent {...textareaProps} />
                    {children}
                </ValueContainerElement>
                {accessibilityMarkup}
            </TextareaContainerElement>
        );
    }
);
